import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  useTheme,
  FormHelperText,
  Autocomplete,
  TextField,
  Chip,
} from "@mui/material";
import { Add } from "@mui/icons-material";
import Swal from "sweetalert2";
import { createFinancialTransactionsForAutoAccrual } from "../../api/financialTransactions/createFinancialTransactionsForAutoAccrual";
import { getBankStatement } from "../../api/financialTransactions/getBankStatement";
import { AgGridReact } from "ag-grid-react";
import { createTurkishFilterParams } from "../../utils/agGridTextFilterTr/agGridFilterUtils";
import localeTextTR from "../../locale.tr";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
export const BankStatementModal = ({
  open,
  onClose,
  taxpayerId,
  endDate,
  startDate,
}) => {
  const theme = useTheme();
  const [localStartDate, setStartDate] = useState(startDate);
  const [localEndDate, setEndDate] = useState(endDate);
  const [rowData, setRowData] = useState([]);

  useEffect(() => {
    handleSearch(taxpayerId, endDate, startDate);
  }, []);

  const initialFormState = {
    periods: [],
    year: new Date().getFullYear(),
  };

  const handleSearch = async (taxpayerId, endDate, startDate) => {
    setLoading(true);
    await getBankStatement(taxpayerId, startDate, endDate)
      .then((resp) => {
        setRowData(resp.data.data);
        console.log(resp.data.data, "resp.data.data");
      })
      .catch((err) => {
        console.log(err.response.data);
      })
      .finally(() => setLoading(false));
  };
  const [formData, setFormData] = useState(initialFormState);
  const handleClose = () => {
    setFormData(initialFormState);
    setErrors({});
    setSubmitted(false);
    onClose();
  };

  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    if (formData.periods.length === 0) {
      newErrors.periods = "En az bir ay seçmelisiniz";
    }

    if (!formData.year) {
      newErrors.year = "Yıl seçimi zorunludur";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async () => {
    setSubmitted(true);

    if (!validateForm()) {
      setTimeout(() => {
        setSubmitted(false);
      }, 3000);
      return;
    }

    await createFinancialTransactionsForAutoAccrual({ taxpayerId, ...formData })
      .then((res) => {
        Swal.fire({
          text: res.data.message || "Başarıyla Kaydedildi.",
          icon: "success",
          showConfirmButton: false,
        });
      })
      .catch((err) => {
        Swal.fire({
          text: err.response?.data?.message || "Hata oluştu",
          icon: "error",
          showConfirmButton: false,
        });
      });

    handleSearch();
    handleClose();
  };

  const inputStyle = {
    marginBottom: "8px",
    "& .MuiInputLabel-root": {
      transform: "translate(14px, 12px) scale(1)",
      "&.MuiInputLabel-shrink": {
        transform: "translate(14px, -9px) scale(0.75)",
      },
    },
  };
  const [columnDefs] = useState([
    {
      field: "date",
      headerName: "Tarih",
      sortable: true,
      filter: false,
      flex: 1,
    },
    {
      field: "documentType",
      headerName: "İşlem Türü",
      sortable: true,
      filter: true,
      minWidth: 140,
    },
    {
      field: "paymentType",
      headerName: "Ödeme Türü",
      sortable: true,
      filter: true,
    },
    {
      field: "description",
      headerName: "Açıklama",
      sortable: true,
      filter: "agTextColumnFilter",
      flex: 1,
    },
    {
      field: "amount",
      headerName: "Tutar",
      sortable: true,
      filter: false,
      valueFormatter: (params) => {
        return params.value.toFixed(2) + " ₺";
      },
      type: "numericColumn",
      flex: 1,
    },
    {
      field: "balance",
      headerName: "Bakiye",
      sortable: true,
      filter: false,
      valueFormatter: (params) => {
        return params.value.toFixed(2) + " ₺";
      },
      flex: 1,
      type: "numericColumn",
    },
  ]);
  const defaultColDef = useMemo(
    () => ({
      width: 90,
      filter: true,
      sortable: true,
      resizable: true,
      floatingFilter: true,
      suppressMenu: true,
      filterParams: createTurkishFilterParams(),
    }),
    []
  );
  const [loading, setLoading] = useState(true);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: "12px",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
        },
      }}
    >
      <DialogTitle
        sx={{
          background: theme.palette.primary.main,
          color: "white",
          fontWeight: 500,
          padding: "6px 24px",
        }}
      >
        Ekstre{" "}
      </DialogTitle>
      <DialogContent sx={{ padding: "20px", mt: "12px" }}>
        <Box
          sx={{
            flexGrow: 1,
            px: 2,
            pb: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            className="ag-theme-quartz"
            style={{
              height: "400px",
              width: "%100",
            }}
          >
            <AgGridReact
              rowData={rowData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              animateRows={true}
              localeText={localeTextTR}
              rowSelection="multiple"
              loading={loading}
            />
          </div>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{ padding: "16px 24px", justifyContent: "flex-end", gap: "12px" }}
      >
        <Button
          onClick={handleClose}
          color="error"
          variant="contained"
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 500,
            minWidth: "100px",
          }}
        >
          Kapat
        </Button>
      </DialogActions>
    </Dialog>
  );
};
